import { DotLottieReact, type DotLottie } from '@lottiefiles/dotlottie-react';
import type { AlertColor } from '@mui/material/Alert';
import { ReactNode, useCallback, useState } from 'react';
import { usePrefersReducedMotion } from '../../hooks/use-prefers-reduced-motion';
import { Button } from '../button';
import { MuiAlert, MuiAlertTitle } from '../mui-alert';
import { Stack } from '../stack';
import { Typography } from '../typography';
import { EmptyStateSize, IMAGE_MAP, isLottieFile, type EmptyStateImage } from './utils';

type EmptyStateWarning = {
	/**
	 * The title displayed in the alert header.
	 */
	title?: string;
	/**
	 * The body text of the alert.
	 */
	description?: string;
	/**
	 * The severity level of the alert.
	 */
	severity?: AlertColor;
};

const IMAGE_WIDTH: Record<EmptyStateSize, string> = {
	// XLarge and Large share the same image width; the difference between these variants is the text width below.
	[EmptyStateSize.XLarge]: '600px',
	[EmptyStateSize.Large]: '600px',
	[EmptyStateSize.Medium]: '280px',
	[EmptyStateSize.Small]: '110px'
};

const TEXT_WIDTH: Record<EmptyStateSize, string> = {
	[EmptyStateSize.XLarge]: '600px',
	[EmptyStateSize.Large]: '330px',
	[EmptyStateSize.Medium]: '280px',
	[EmptyStateSize.Small]: '110px'
};

export type EmptyStateProps = {
	/**
	 * Controls the width of the empty state image.
	 * @default 'large'
	 */
	size?: EmptyStateSize;
	/**
	 * The main heading of the empty state. Accepts a string (rendered as h4 Typography)
	 * or a custom ReactNode for full styling control.
	 */
	title: ReactNode;
	/**
	 * The descriptive body text shown below the title. Accepts a string (rendered as body1
	 * Typography) or a custom ReactNode for full styling control.
	 */
	description?: ReactNode;
	/**
	 * The name of the SVG image to display above the title.
	 */
	image?: EmptyStateImage;
	/**
	 * Optional warning / info alert shown below the description.
	 */
	warning?: EmptyStateWarning;
	/**
	 * URL opened in a new tab when the link button is clicked.
	 */
	link?: string;
	/**
	 * Label for the optional external link button.
	 */
	linkButtonTitle?: string;
	/**
	 * Additional action buttons rendered alongside the link button.
	 */
	extraButtons?: ReactNode;
	/**
	 * Optional caption text shown below the description (rendered as body2 typography).
	 */
	captionText?: string;
	/**
	 * Overrides the max-width of the text content area, allowing text to exceed the image
	 * width. Useful with smaller image sizes (e.g. Small) where the default text width
	 * would otherwise be too narrow.
	 */
	textMaxWidth?: string;
};

export const EmptyState = ({
	size = EmptyStateSize.Large,
	title,
	description,
	image,
	warning,
	link,
	linkButtonTitle,
	extraButtons,
	captionText,
	textMaxWidth
}: EmptyStateProps) => {
	const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);
	const prefersReducedMotion = usePrefersReducedMotion();

	const handleMouseEnter = () => {
		if (prefersReducedMotion) return;
		dotLottie?.setFrame(0);
		dotLottie?.play();
	};

	const dotLottieRefCallback = useCallback(
		(instance: DotLottie | null) => {
			setDotLottie(instance);
			if (instance && prefersReducedMotion) {
				const showLastFrame = () => instance.setFrame(instance.totalFrames - 1);
				if (instance.isLoaded) {
					showLastFrame();
				} else {
					instance.addEventListener('load', showLastFrame);
				}
			}
		},
		[prefersReducedMotion]
	);

	const resolvedTextMaxWidth = textMaxWidth ?? TEXT_WIDTH[size];
	const outerWidth = textMaxWidth ? `max(${IMAGE_WIDTH[size]}, ${textMaxWidth})` : IMAGE_WIDTH[size];

	return (
		<Stack
			direction="column"
			justifyContent="center"
			alignItems="center"
			style={{ height: '100%', marginLeft: 'auto', marginRight: 'auto', width: outerWidth }}
		>
			{image ? (
				isLottieFile(IMAGE_MAP[image]) ? (
					<div
						style={{ alignSelf: 'center', marginBottom: '1rem', width: IMAGE_WIDTH[size], cursor: 'pointer' }}
						onMouseEnter={handleMouseEnter}
					>
						<DotLottieReact src={IMAGE_MAP[image]} autoplay={!prefersReducedMotion} dotLottieRefCallback={dotLottieRefCallback} />
					</div>
				) : (
					<img
						src={IMAGE_MAP[image]}
						alt={typeof title === 'string' ? title : ''}
						style={{ alignSelf: 'center', marginBottom: '1rem', width: IMAGE_WIDTH[size] }}
					/>
				)
			) : null}
			<Stack direction="column" alignItems="center" gap={0} style={{ maxWidth: resolvedTextMaxWidth }}>
				{typeof title === 'string' ? (
					<Typography variant="h4" style={{ textAlign: 'center' }}>
						{title}
					</Typography>
				) : (
					title
				)}
				{typeof description === 'string' ? (
					<Typography variant="body1" style={{ textAlign: 'center', marginBottom: '1rem' }}>
						{description}
					</Typography>
				) : (
					description
				)}
				{captionText ? (
					<Typography variant="body2" style={{ textAlign: 'center', marginBottom: '1rem' }}>
						{captionText}
					</Typography>
				) : null}
				{warning ? (
					<MuiAlert severity={warning.severity ?? 'info'}>
						{warning.title ? <MuiAlertTitle>{warning.title}</MuiAlertTitle> : null}
						{warning.description ?? null}
					</MuiAlert>
				) : null}
				{(link ?? extraButtons) ? (
					<Stack direction="row" justifyContent="center" alignItems="center" gap={1}>
						{link ? (
							<Button endIconName="OpenInNew" size="medium" onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}>
								{linkButtonTitle ?? 'Learn more'}
							</Button>
						) : null}
						{extraButtons ?? null}
					</Stack>
				) : null}
			</Stack>
		</Stack>
	);
};
